import React, { useEffect, useState, useCallback } from "react";
import { Grid, Box, Button, useTheme, useMediaQuery, Tabs, Tab } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useDispatchWrapper } from "../../hooks";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
} from "../../redux/messages/messagesActions";
import { ManageQuickReplyTemplateRightPanel } from "./ManageQuickReplyTemplateRightPanel";
import { useBillingCompanyContext } from "../../providers";
import { useDebounce } from "../../hooks/useDebounce";
import { PageLoader } from "../../components/PageLoader/PageLoader";
import { LoadState } from "../../constants/enums";
import { ViewToggle } from "../../components/ViewToggle";
import { useReduxStore } from "../../redux/store.hooks";
import { ControlledCustomSelect } from "../../components/formsComponents";
import { StandardCard } from "../../components/Cards";
import { Breadcrumb } from "../../layout/fullLayout/breadcrumb/Breadcrumb";
import { IBreadcrumbProps } from "../../layout/fullLayout/breadcrumb/IBreadcrumbProps";
import { fetchQuickReplyTemplateListAsync } from "../../redux/quickReplyTemplates/quickReplyTemplateAction";
import { IQuickReplyTemplateData } from "../../redux/quickReplyTemplates/quickReplyTemplate.types";
import { INITIAL_QUICK_REPLY_TEMPLATE_DATA } from "../../redux/quickReplyTemplates/defaultState";
import { QuickReplyTemplatesAutoSearch } from "../../components/AutoCompleteSearches/QuickReplyTemplatesAutoSearch";
import { QuickTemplateTable } from "./components/QuickTemplateTable";
import { QuickReplyTemplateCardsV2 } from "./components/QuickReplyTemplateCardsV2";
import { stripHtmlTags } from "../../helpers/stripHtmlTags";
import { VIEWS_CACHING_KEYS } from "../../constants/constants";
import { useDataTableV2Context } from "../../components/TableV2/context/DataTableV2Provider";
import { useAuth } from "../../contexts/AuthProvider/AuthProvider";

// 🔹 Utility: normalize quick template into consistent shape
const normalizeTemplate = (template: any): IQuickReplyTemplateData => {
  const components = template.components || [];
  return {
    ...template,
    header: components.find((c: any) => c.type === "HEADER") || {
      type: "HEADER",
      text: "",
      format: null,
    },
    body: components.find((c: any) => c.type === "BODY") || {
      type: "BODY",
      text: "",
    },
    footer: components.find((c: any) => c.type === "FOOTER") || {
      type: "FOOTER",
      text: "",
    },
    buttons: components.find((c: any) => c.type === "BUTTONS") || {
      type: "BUTTONS",
      buttons: [],
    },
  };
};

interface IQuickReplyTemplateListV2Props {
  onTemplateDetails?: (template: IQuickReplyTemplateData) => void;
  selectedTemplateUuid?: string | null;
  showCreateButton?: boolean;
  searchTemplateName?: string;
  onSearchChange?: (searchName: string) => void;
  toNumber?: string;
  isFromChat?: boolean;
  onClose?: () => void;
  onCreateClick?: () => void;
  hideCreateButton?: boolean;
  compactCardsView?: boolean;
  refetchRef?: React.MutableRefObject<(() => void) | null>;
  onSelectTemplate?: (message: string, template?: IQuickReplyTemplateData) => void;
  showCreatePanel?: boolean;
  hideEditIcon?: boolean;
}

export const QuickReplyTemplateListV2: React.FC<
  IQuickReplyTemplateListV2Props
> = (props) => {
  const { saveDataInCache, cachingDataState, getCachedDataByKey } =
    useDataTableV2Context();

  const {
    onTemplateDetails,
    selectedTemplateUuid = null,
    showCreateButton = true,
    searchTemplateName: externalSearchName,
    onSearchChange,
    toNumber,
    isFromChat = false,
    onClose,
    onCreateClick,
    hideCreateButton = false,
    compactCardsView = false,
    refetchRef,
    onSelectTemplate,
    showCreatePanel = false,
    hideEditIcon = false,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useAuth();

  const BCrumb: IBreadcrumbProps["items"] = [
    {
      to: "/dashboard",
      title: "dashboard",
    },
    {
      to: "/quick-reply-templates",
      title: "Quick Reply Templates",
    },
  ];

  const dispatch = useDispatchWrapper();
  const { comapny } = useBillingCompanyContext() || {};
  const company_outlet_uuid = comapny?.company_outlet_uuid;

  const { list: quickReplyTemplateList, loading: dataLoading } =
    useReduxStore("quickReplyTemplate").list;
  const { totalRecords: storeTotalRecords } =
    useReduxStore("quickReplyTemplate").list;

  const [channels, setChannels] = useState<IQuickReplyTemplateData[]>([]);
  const [openManageWhatsAppTemplatePanel, setOpenManageWhatsAppTemplatePanel] =
    useState<IQuickReplyTemplateData | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [internalSearchName, setInternalSearchName] = useState("");
  const [status, setStatus] = React.useState("ACTIVE");
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showCardsView, setShowCardsView] = useState(true);
  const [scope, setScope] = useState<"own" | "public">("public");

  const searchTemplateName =
    externalSearchName !== undefined ? externalSearchName : internalSearchName;

  const debouncedSearchName = useDebounce(searchTemplateName, 500);

  const location = useLocation();

  const getChannels = useCallback(
    async (
      channelType: string,
      showLoader?: boolean,
      direction?: "next" | "prev",
      newRowsPerPage?: number,
    ) => {
      try {
        if (showLoader) dispatch(saveLoaderProgress());
        // setChannels([]);

        if (channelType === "WHATSAPP") {
          let page = currentPage;
          if (direction === "next") page++;
          else if (direction === "prev") page = Math.max(1, page - 1);
          else page = 1;
          setCurrentPage(page);

          let filterColumn: string | undefined = undefined;
          let filterValue: string | undefined = undefined;

          if (location.pathname.startsWith("/chats")) {
            filterColumn = "tags";
            filterValue = "chat";
          } else if (location.pathname.startsWith("/event-leads")) {
            filterColumn = "tags";
            filterValue = "leads";
          }

          await dispatch(
            fetchQuickReplyTemplateListAsync(
              page,
              // rowsInPerPage
              newRowsPerPage || rowsPerPage,
              // limit
              rowsPerPage,
              // status
              status,
              // date
              { fromDate: "", toDate: "" },
              // searchValue (advanced filters)
              [],
              // company_outlet_uuid
              company_outlet_uuid,
              // searchByName
              debouncedSearchName,
              // searchByTemplateId
              undefined,
              //template_for
              "QUICK_REPLY",
              undefined,
              scope === "own" ? user?.userUuid : undefined,
              filterColumn,
              filterValue,
              scope === "public" ? "public" : undefined,
            ),
          );
        }
      } finally {
        dispatch(saveLoaderCompleted());
      }
    },
    [
      company_outlet_uuid,
      debouncedSearchName,
      currentPage,
      rowsPerPage,
      status,
      scope,
      user?.userUuid,
      dispatch,
      location.pathname,
    ],
  );

  const handleWhatsappMessageSend = (
    template: IQuickReplyTemplateData,
    toNumber?: string,
  ) => {
    if (onSelectTemplate) {
      const message = stripHtmlTags(template.text || "");
      onSelectTemplate(message, template);
      // Note: The template.source_table presence check below to decide onClose is redundant
      // because the parent SelectQuickReplyTemplate container always calls onClose() right after.
      if (!template.source_table) {
        onClose?.();
      }
      return;
    }
  };

  const handleSearchChange = (template: any, inputValue: string) => {
    const newSearchName = inputValue || template?.name || "";

    if (onSearchChange) {
      onSearchChange(newSearchName);
    } else {
      setInternalSearchName(newSearchName);
    }
  };

  const handleCreate = () => {
    setOpenManageWhatsAppTemplatePanel(INITIAL_QUICK_REPLY_TEMPLATE_DATA);
  };

  const handleShowCard = (value: boolean) => () => {
    saveDataInCache(VIEWS_CACHING_KEYS.QUICK_REPLY_TEMPLATE_GRID_TABLE_KEY, {
      showCard: value,
    });
    setShowCardsView(value);
  };

  useEffect(() => {
    setChannels((quickReplyTemplateList || []).map(normalizeTemplate));
  }, [quickReplyTemplateList]);

  useEffect(() => {
    if (storeTotalRecords !== undefined) {
      setTotalRecords(storeTotalRecords);
    }
  }, [storeTotalRecords]);

  useEffect(() => {
    getChannels("WHATSAPP");
  }, [debouncedSearchName, status, scope]);

  useEffect(() => {
    const data = getCachedDataByKey(
      VIEWS_CACHING_KEYS.QUICK_REPLY_TEMPLATE_GRID_TABLE_KEY,
    );
    setShowCardsView(data ? data.showCard : true);
  }, [
    cachingDataState[VIEWS_CACHING_KEYS.QUICK_REPLY_TEMPLATE_GRID_TABLE_KEY],
  ]);

  // Expose refetch function to parent via ref
  useEffect(() => {
    if (refetchRef) {
      refetchRef.current = () => getChannels("WHATSAPP", true);
    }
  }, [refetchRef, getChannels]);

  return (
    <>
      {showCreateButton && (
        <Breadcrumb
          title="View Quick Reply Template"
          items={BCrumb}
          actionButtonText="Create Quick Reply Template"
          actionButtonColor="success"
          onActionButtonClick={handleCreate}
        />
      )}

      <StandardCard>
        <PageLoader loading={dataLoading === LoadState.InProgress}>
          <Grid container spacing={isMobile ? 1 : 5} justifyContent="center">
            <Grid item xs={12}>
              <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs
                  value={scope}
                  onChange={(_, newValue) => setScope(newValue)}
                  aria-label="template scope tabs"
                  sx={{
                    "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0" },
                    "& .MuiTab-root": { fontWeight: "bold", fontSize: "0.9rem", textTransform: "none" },
                  }}
                >
                  <Tab label="Public" value="public" />
                  <Tab label="Own" value="own" />
                </Tabs>
              </Box>
              {isMobile ? (
                <Box sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 0.75,
                      mb: 0.75,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <QuickReplyTemplatesAutoSearch
                        label="Search by Template Name"
                        value={searchTemplateName}
                        onSelect={handleSearchChange}
                      />
                    </Box>
                    <Box sx={{ width: 105, flexShrink: 0 }}>
                      <ControlledCustomSelect
                        label="Status"
                        value={status}
                        options={[
                          { label: "All", value: "" },
                          { label: "Active", value: "ACTIVE" },
                          { label: "Inactive", value: "INACTIVE" },
                        ]}
                        onChange={(e) => setStatus(e.target.value as string)}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: 0.75,
                    }}
                  >
                    <ViewToggle
                      isGridView={showCardsView}
                      onToggle={handleShowCard(!showCardsView)}
                    />
                    {!showCreateButton && !hideCreateButton && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={onCreateClick || handleCreate}
                      >
                        Create
                      </Button>
                    )}
                  </Box>
                </Box>
              ) : (
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 600,
                      fontWeight: "bold",
                      ml: 2,
                      display: "flex",
                      gap: 2,
                      mt: 1,
                    }}
                  >
                    <QuickReplyTemplatesAutoSearch
                      label="Search by Template Name"
                      value={searchTemplateName}
                      onSelect={handleSearchChange}
                    />
                    <Box sx={{ display: "flex", gap: 2, width: "50%" }}>
                      <ControlledCustomSelect
                        label="Status"
                        value={status}
                        options={[
                          { label: "All", value: "" },
                          { label: "Active", value: "ACTIVE" },
                          { label: "Inactive", value: "INACTIVE" },
                        ]}
                        onChange={(e) => setStatus(e.target.value as string)}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mr: 2,
                    }}
                  >
                    <ViewToggle
                      isGridView={showCardsView}
                      onToggle={handleShowCard(!showCardsView)}
                    />
                    {!showCreateButton && !hideCreateButton && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={onCreateClick || handleCreate}
                      >
                        Create Quick Reply
                      </Button>
                    )}
                  </Box>
                </Grid>
              )}

              <Box sx={{ width: "100%", p: isMobile ? 0.5 : 2 }}>
                {/* Template List/Cards View */}
                {showCardsView ? (
                  <QuickReplyTemplateCardsV2
                    templates={channels}
                    onTemplateDetails={onTemplateDetails || (() => { })}
                    selectedTemplate={selectedTemplateUuid}
                    onWhatsappMessageSend={(template) => {
                      handleWhatsappMessageSend(template, toNumber);
                    }}
                    toNumber={toNumber}
                    currentPage={currentPage}
                    totalRecords={totalRecords}
                    onPageChange={(dir, _, perPage) =>
                      getChannels("WHATSAPP", true, dir, perPage)
                    }
                    onRowsPerPageChange={setRowsPerPage}
                    rowsPerPage={rowsPerPage}
                    onTemplatePreview={() => () => { }}
                    isFromQuickReply={true}
                    isFromChat={isFromChat}
                    showCreatePanel={showCreatePanel}
                    compactView={compactCardsView || isMobile}
                    onEditClick={hideEditIcon ? undefined : (template) => {
                      setOpenManageWhatsAppTemplatePanel(template);
                    }}
                  />
                ) : (
                  <QuickTemplateTable
                    fetchList={() => getChannels("WHATSAPP", true)}
                    templates={channels}
                    selectedTemplate={selectedTemplateUuid}
                    onWhatsappMessageSend={(template) => {
                      handleWhatsappMessageSend(template, toNumber);
                    }}
                    toNumber={toNumber}
                    currentPage={currentPage}
                    totalRecords={totalRecords}
                    onPageChange={(dir, _, perPage) =>
                      getChannels("WHATSAPP", true, dir, perPage)
                    }
                    onRowsPerPageChange={setRowsPerPage}
                    rowsPerPage={rowsPerPage}
                    onTemplatePreview={() => () => { }}
                    isFromQuickReply={true}
                    isFromChat={isFromChat}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </PageLoader>
      </StandardCard>
      {openManageWhatsAppTemplatePanel && (
        <ManageQuickReplyTemplateRightPanel
          open
          templateData={openManageWhatsAppTemplatePanel.quick_reply_uuid ? openManageWhatsAppTemplatePanel : undefined}
          onClose={() => setOpenManageWhatsAppTemplatePanel(null)}
          onSaveSuccess={() => {
            setOpenManageWhatsAppTemplatePanel(null);
            getChannels("WHATSAPP", true);
          }}
        />
      )}
    </>
  );
};
